import React, {Component, ReactNode} from 'react';

import {TimeFormatsEnum} from '../../../../libs/shared/core/src/helpers/datetime/dateTimeHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {FrontEvent} from '../../../../libs/shared/core/src/helpers/react/reactHelpers';
import {
  InterfaceEventsEnum,
  SnoozeLocationsEnum,
  trackInterfaceEvent,
} from '../../../../libs/shared/core/src/services/tracking/tracking';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Dropdown, DropdownProps} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {
  FutureTimeSourcesEnum,
  OnFutureTimeValueSelectOptions,
} from '../../../../srcCommon/models/futureTimeDropdownTypes';
import {CustomSnoozeModel} from '../../../models/api/customSnoozeModel';
import {FutureTimeValuesEnum} from '../../../models/api/futureTimeValueModel';
import {ReminderPresets} from '../../../models/api/preferencesModel';
import {ReminderModel} from '../../../models/api/reminderModel';
import {FutureTimeDropdownCustom, FutureTimeDropdownCustomPublicProps} from './futureTimeDropdownCustom';
import {FutureTimeDropdownPresets} from './futureTimeDropdownPresets';

/*
 * Props.
 */

export interface FutureTimeDropdownProps extends DropdownProps, FutureTimeDropdownCustomPublicProps {
  title: ReactNode;
  values: ReadonlyArray<FutureTimeValuesEnum>;
  reminderPresets: ReminderPresets | null;
  customSnoozes: ReadonlyArray<CustomSnoozeModel> | null;
  customSnoozePreset?: ReminderModel;
  onValueSelect?: (arg: number) => void;
  onValueClear?: () => void;
  onOpenSnoozesClick?: () => void;
  location?: SnoozeLocationsEnum;
  timeFormat: TimeFormatsEnum;

  shouldStartOnCustom?: boolean;
  shouldHideDropdownHeader?: boolean;
  onBackClick?: () => void;
  backLabel?: ReactNode;
}

/*
 * Component.
 */

interface FutureTimeDropdownState {
  showCustom: boolean;
}

/* eslint class-methods-use-this: 0 */
export class FutureTimeDropdown extends Component<FutureTimeDropdownProps, FutureTimeDropdownState> {
  constructor(props: FutureTimeDropdownProps) {
    super(props);
    this.state = {showCustom: props.shouldStartOnCustom || false};
  }

  /*
   * Event Handlers.
   */

  private readonly onValueSelect: (options: OnFutureTimeValueSelectOptions) => void = ({
    datetime,
    source,
  }) => {
    const {onValueSelect, location} = this.props;

    // Send an interface event
    if (onValueSelect && location) {
      trackInterfaceEvent(InterfaceEventsEnum.SNOOZE_CONVERSATION, {
        location,
        expiration_timestamp: datetime,
        time_option: source,
      });
    }

    // Notify the parent.
    if (onValueSelect) {
      onValueSelect(datetime);
    }
  };

  private readonly onClearValue = () => {
    const {onValueClear} = this.props;

    // Notify the parent.
    if (onValueClear) {
      onValueClear();
    }
  };

  private readonly toggleShowDatePicker: (event: FrontEvent) => void = (event) => {
    this.setState(({showCustom}) => ({showCustom: !showCustom}));
    event.preventDefault();
  };

  /*
   * Render.
   */

  render() {
    const {showCustom} = this.state;
    const {value} = this.props;
    const content = showCustom || value ? this.renderCustom() : this.renderUserPresets();

    return (
      <Dropdown {...this.props} shouldSkipApplyMaxSize>
        {(!this.props.shouldHideDropdownHeader || showCustom) && this.renderHeader()}
        {content}
      </Dropdown>
    );
  }

  private renderHeader() {
    return (
      <DropdownHeader
        title={this.props.title}
        leftIcon={this.maybeRenderBackButton()}
        rightIcon={this.maybeRenderSettingsButton()}
      />
    );
  }

  private maybeRenderBackButton() {
    const {showCustom} = this.state;
    const {value, shouldStartOnCustom, onRequestClose, backLabel, onBackClick} = this.props;

    // If `props.onBack` is passed, show the back button even when not in custom
    // mode (i.e. on the first screen).
    if (onBackClick && backLabel && !showCustom) {
      return (
        <IconButton
          iconName="chevronLeftSmall"
          label={backLabel}
          onClick={onBackClick}
          size={VisualSizesEnum.SMALL}
        />
      );
    }

    // We only need the button when in custom mode.
    if (!showCustom || value) {
      return null;
    }

    // Render a button to go back to preset mode.
    return (
      <IconButton
        iconName="chevronLeftSmall"
        label={<Trans id="futuretimedropdown-back">Back to presets</Trans>}
        onClick={shouldStartOnCustom ? onRequestClose : this.toggleShowDatePicker}
        size={VisualSizesEnum.SMALL}
      />
    );
  }

  private maybeRenderSettingsButton() {
    // We only need the button when in v2 list mode.
    const {onOpenSnoozesClick} = this.props;
    const {showCustom} = this.state;

    if (showCustom || !onOpenSnoozesClick) {
      return null;
    }

    return (
      <UserThemeConsumer>
        {(theme) => (
          <IconButton
            iconName="gearSmall"
            label={<Trans id="futuretimedropdown-changepresets">Change snooze presets</Trans>}
            onClick={onOpenSnoozesClick}
            size={VisualSizesEnum.SMALL}
            color={theme.greys.shade80}
            hoverColor={theme.greys.shade80}
          />
        )}
      </UserThemeConsumer>
    );
  }

  /*
   * Future time value presets.
   */

  private renderUserPresets() {
    return (
      <FutureTimeDropdownPresets
        {...this.props}
        onValueSelect={this.onValueSelect}
        showCustom={this.toggleShowDatePicker}
      />
    );
  }

  /*
   * Custom snooze.
   */

  private renderCustom() {
    const onCustomValueSelect = (datetime: number) => {
      this.onValueSelect({datetime, source: FutureTimeSourcesEnum.CUSTOM});
    };
    return (
      <FutureTimeDropdownCustom
        {...this.props}
        onValueSelect={onCustomValueSelect}
        onValueClear={this.onClearValue}
        hideDateTimePicker={this.toggleShowDatePicker}
        timeFormat={this.props.timeFormat}
      />
    );
  }
}
